<template>
  <div class="help-launcher">
    <button
      ref="trigger"
      type="button"
      class="icon-button help-launcher__button"
      :aria-expanded="open ? 'true' : 'false'"
      aria-haspopup="dialog"
      aria-label="Help for this page"
      title="Help"
      @click="toggle"
    >
      <i class="fas fa-question" aria-hidden="true"></i>
      <span v-if="pageArticles.length" class="help-launcher__dot" aria-hidden="true"></span>
    </button>

    <Teleport to="body">
      <div v-if="open" class="help-drawer__backdrop" @click="close"></div>
      <aside
        v-if="open"
        ref="drawer"
        class="help-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-drawer-title"
        @keydown="onKeydown"
      >
        <header class="help-drawer__header">
          <h2 id="help-drawer-title" class="help-drawer__title">Help</h2>
          <button ref="closeButton" type="button" class="help-drawer__close" aria-label="Close help" @click="close">
            <i class="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        <div class="help-drawer__body">
          <HelpSearch placeholder="Search the guides" autofocus @navigate="close" />

          <section v-if="pageArticles.length" class="help-drawer__section" aria-labelledby="help-drawer-page">
            <h3 id="help-drawer-page" class="help-drawer__subtitle">Guides for this page</h3>
            <ul class="help-drawer__list">
              <li v-for="article in pageArticles" :key="article.slug">
                <router-link :to="`/help/${article.slug}`" @click="close">
                  <span class="help-drawer__link-title">{{ article.title }}</span>
                  <span class="help-drawer__link-summary">{{ article.summary }}</span>
                </router-link>
              </li>
            </ul>
          </section>
          <p v-else-if="contextLoaded" class="help-drawer__empty">
            There's no guide for this page yet. Search above, or browse all guides.
          </p>

          <router-link to="/help" class="help-drawer__all" @click="close">
            <i class="fas fa-book-open" aria-hidden="true"></i> Browse all guides
          </router-link>
        </div>
      </aside>
    </Teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import HelpSearch from './HelpSearch.vue'
import { articlesForRoute, fetchHelpContextIndex } from '@/utils/helpApi'

const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'

/** Top-bar Help: guides for the current page (dot when there are some) plus search. */
export default {
  name: 'HelpLauncher',
  components: { HelpSearch },
  data() {
    return { open: false, contextIndex: null, contextLoaded: false }
  },
  computed: {
    ...mapGetters(['currentUser']),
    pageArticles() {
      return articlesForRoute(this.contextIndex, this.$route)
    },
  },
  watch: {
    currentUser: { immediate: true, handler: 'loadContext' },
    $route() {
      this.open = false
    },
  },
  methods: {
    async loadContext() {
      if (!this.currentUser) return
      try {
        this.contextIndex = await fetchHelpContextIndex(this.currentUser)
      } catch {
        this.contextIndex = null
      } finally {
        this.contextLoaded = true
      }
    },
    toggle() {
      if (this.open) this.close()
      else this.open = true
    },
    close() {
      if (!this.open) return
      this.open = false
      this.$nextTick(() => this.$refs.trigger?.focus())
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault()
        this.close()
        return
      }
      if (event.key !== 'Tab') return
      const nodes = Array.from(this.$refs.drawer?.querySelectorAll(FOCUSABLE) || [])
      if (!nodes.length) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    },
  },
}
</script>

<style scoped>
.help-launcher { display: inline-flex; position: relative; }
.help-launcher__button { position: relative; }
.help-launcher__dot {
  background: var(--clinch-blue-primary, #33b5ff);
  border: 2px solid var(--card, #fff);
  border-radius: 999px;
  height: 10px;
  position: absolute;
  right: 4px;
  top: 4px;
  width: 10px;
}

.help-drawer__backdrop { background: rgba(0, 20, 40, 0.35); inset: 0; position: fixed; z-index: 2000; }
.help-drawer {
  background: var(--background, #fff);
  box-shadow: -8px 0 30px rgba(0, 31, 63, 0.18);
  display: flex;
  flex-direction: column;
  height: 100dvh;
  max-width: 100vw;
  position: fixed;
  right: 0;
  top: 0;
  width: 26rem;
  z-index: 2001;
}
.help-drawer__header {
  align-items: center;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  padding: 0.9rem 1.1rem;
}
.help-drawer__title { font-size: 1.1rem; font-weight: 800; margin: 0; }
.help-drawer__close {
  background: none;
  border: 0;
  border-radius: 8px;
  color: var(--muted-foreground);
  cursor: pointer;
  height: 36px;
  width: 36px;
}
.help-drawer__close:hover,
.help-drawer__close:focus-visible { background: var(--muted); color: var(--foreground); outline: none; }
.help-drawer__body { flex: 1; overflow-y: auto; padding: 1rem 1.1rem 1.5rem; }
.help-drawer__section { margin-top: 1.25rem; }
.help-drawer__subtitle {
  color: var(--muted-foreground);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  margin: 0 0 0.5rem;
  text-transform: uppercase;
}
.help-drawer__list { list-style: none; margin: 0; padding: 0; }
.help-drawer__list a {
  border-radius: 8px;
  color: var(--foreground);
  display: block;
  padding: 0.55rem 0.6rem;
  text-decoration: none;
}
.help-drawer__list a:hover,
.help-drawer__list a:focus-visible { background: var(--muted); outline: none; }
.help-drawer__link-title { display: block; font-weight: 700; }
.help-drawer__link-summary { color: var(--muted-foreground); display: block; font-size: 0.85rem; }
.help-drawer__empty { color: var(--muted-foreground); font-size: 0.9rem; margin: 1.25rem 0 0; }
.help-drawer__all {
  align-items: center;
  display: inline-flex;
  font-weight: 700;
  gap: 0.5rem;
  margin-top: 1.5rem;
  min-height: 32px;
}
</style>
